import React from 'react';
import styled from 'styled-components';
import { useTransition, animated } from 'react-spring';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import Dismiss from './components/Dismiss';

/**
 * NOTE: This is meant to replace nth-block-alert for the purpose of rendering
 *       the address verification error message. There were a couple things that
 *       needed to be addressed for this nth-block-alert use case specifically:
 *       1) The styles of nth-block-alert prioritize `alertNoneNone` over error
 *          styles. As a side note, the error styles here should probably made
 *          themeable. For now, I updated the error styles to match Alice's designs.
 *       2) Though it makes sense that `nth-block-alert` does not have a
 *          role="alert" by default, I think for accessibility purposes, adding
 *          role="alert" is a minimum requirement. Could also make the alert tabbable
 *          by javascript and bring focus to the message on error, but I'm not as
 *          sure about that one.
 *       3) I added style overrides for padding and margin to match the designs.
 */

const Container = styled.div<{
  s?: React.CSSProperties;
  onClick?: () => void;
  error?: boolean;
}>(({ s, onClick, error }) => ({
  cursor: onClick ? 'pointer' : '',
  backgroundColor: '#fff',
  padding: '1.25rem',
  ...s,
  ...(error && {
    borderColor: '#F2B6B6',
    backgroundColor: '#F2B6B6',
    color: '#000000',
  }),
}));

const DismissContainer = styled.div`
  float: right;
  margin: 1rem;
`;

interface BlockAlertProps {
  children: React.ReactNode;
  show?: boolean;
  focusOnShow?: boolean;
  error?: boolean;
  onClick?: () => void;
  dismissible?: boolean;
  dismiss?: React.ReactNode;
  dismissOnClick?: () => void;
  role?: string;
  s?: React.CSSProperties;
  timeout?: number;
}

const BlockAlert: React.FunctionComponent<BlockAlertProps> = ({
  children,
  show = false,
  focusOnShow = false,
  error = false,
  onClick,
  dismissible = false,
  dismiss,
  dismissOnClick,
  role,
  s = {},
  timeout = 300,
}) => {
  const alertRef = React.useRef<HTMLDivElement>(null);
  const {
    alertNoneNone: alertContainerStyle,
    buttonsDismissNone: buttonsDismissStyle,
  } = useTheme();

  const transitions = useTransition(show, null, {
    from: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: timeout },
  });

  React.useEffect(() => {
    if (show && focusOnShow) alertRef?.current?.focus();
  }, [show]);

  return (
    <div role={role} ref={alertRef} tabIndex={focusOnShow ? -1 : undefined}>
      {transitions.map(
        ({ item, key, props }) =>
          item && (
            <animated.div key={key} style={props}>
              {dismissible && (
                <DismissContainer>
                  {dismiss || (
                    <Dismiss
                      s={{
                        ...(error && { fill: '#fff' }),
                        ...buttonsDismissStyle,
                      }}
                      onClick={dismissOnClick}
                    />
                  )}
                </DismissContainer>
              )}
              <Container
                s={{
                  ...alertContainerStyle,
                  ...s,
                }}
                onClick={onClick}
                error={error}
              >
                {children}
              </Container>
            </animated.div>
          ),
      )}
    </div>
  );
};

export default withThemeProvider(BlockAlert, {
  themeTokens: ['alertNoneNone', 'buttonsDismissNone'],
});
